import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { UserButton, useClerk } from "@clerk/react";
import { toast } from "sonner";
import { Download, Archive, Loader2, Check, FileText, Users, Tag, LogOut, Eye } from "lucide-react";
import { bfPublicUrl, fetchBFAdmin } from "@/lib/bf";
import { isPro } from "@/lib/pricing";
import { fetchCatalog, saveCatalog, uploadImage, removeImage, saveProfile, downloadPdf, logEvent } from "@/lib/api";
import { compressImage, downloadPng, downloadZip, slug } from "@/lib/media";
import CoverCard from "@/components/cards/CoverCard";
import ProductCard from "@/components/cards/ProductCard";
import ScaledCard from "@/components/studio/ScaledCard";
import Thumbnails from "@/components/studio/Thumbnails";
import ProductPanel from "@/components/studio/ProductPanel";
import CoverPanel from "@/components/studio/CoverPanel";
import PackageCard from "@/components/cards/PackageCard";
import PackagePanel from "@/components/studio/PackagePanel";
import ProfileFields from "@/components/studio/ProfileFields";
import BFAgentBox from "@/components/bf/BFAgentBox";
import { UnassignedDot } from "@/components/bf/BFUnassigned";
import ShareLink from "@/components/studio/ShareLink";

const useStageWidth = () => {
  const calc = () => (window.innerWidth < 1024 ? Math.min(window.innerWidth - 32, 440) : Math.min(((window.innerHeight - 150) * 9) / 16, 520));
  const [w, setW] = useState(calc);
  useEffect(() => {
    const on = () => setW(calc());
    window.addEventListener("resize", on);
    return () => window.removeEventListener("resize", on);
  }, []);
  return w;
};

export default function Studio({ me }) {
  const isAdmin = me.role === "admin";
  const asAgent = isAdmin && new URLSearchParams(window.location.search).get("ca") === "agent";
  const { signOut } = useClerk();
  const [catalog, setCatalog] = useState(null);
  const [profile, setProfile] = useState({ salon: me.name, whatsapp: me.phone });
  const profileDirty = useRef(false);
  const [selected, setSelected] = useState("cover");
  const [saved, setSaved] = useState(true);
  const [zipProgress, setZipProgress] = useState(null);
  const nodes = useRef({});
  const dirty = useRef(false);
  const stageWidth = useStageWidth();

  useEffect(() => {
    fetchCatalog().then(setCatalog).catch(() => toast.error("Nu am putut încărca catalogul"));
  }, []);

  useEffect(() => {
    if (!catalog || !dirty.current || !isAdmin) return;
    setSaved(false);
    const t = setTimeout(() => {
      saveCatalog(catalog).then(() => setSaved(true)).catch(() => toast.error("Salvarea a eșuat"));
      dirty.current = false;
    }, 700);
    return () => clearTimeout(t);
  }, [catalog]);

  useEffect(() => {
    if (!profileDirty.current) return;
    setSaved(false);
    const t = setTimeout(() => {
      saveProfile({ name: profile.salon, phone: profile.whatsapp }).then(() => setSaved(true)).catch(() => toast.error("Salvarea a eșuat"));
      profileDirty.current = false;
    }, 600);
    return () => clearTimeout(t);
  }, [profile]);

  const onContact = (c) => {
    profileDirty.current = true;
    setProfile(c);
  };

  const update = useCallback((patch) => {
    dirty.current = true;
    setCatalog((c) => ({ ...c, ...patch(c) }));
  }, []);

  const setProduct = (p) => update((c) => ({ products: c.products.map((x) => (x.id === p.id ? p : x)) }));

  const setImage = async (key, image) => {
    if (image) await uploadImage(key, image);
    else await removeImage(key);
    setCatalog((c) =>
      key === "cover" ? { ...c, cover: { ...c.cover, image } } : { ...c, products: c.products.map((x) => (x.id === key ? { ...x, image } : x)) }
    );
  };

  const upload = (key) => async (file) => {
    try {
      await setImage(key, await compressImage(file));
      toast.success("Poza a fost adăugată");
    } catch {
      toast.error("Nu am putut încărca poza");
    }
  };

  const included = useMemo(() => (catalog ? catalog.products.filter((p) => p.include) : []), [catalog]);

  if (!catalog) return <div className="st-loading" data-testid="catalog-loading"><Loader2 className="animate-spin" /> Se încarcă…</div>;
  catalog.contact = profile;

  const indexOf = (p) => included.findIndex((x) => x.id === p.id) + 1;
  const renderCard = (key, ref) => {
    if (key === "cover") return <CoverCard ref={ref} cover={catalog.cover} contact={catalog.contact} />;
    const pk = catalog.packages.findIndex((x) => x.id === key);
    if (pk >= 0) return <PackageCard ref={ref} pack={catalog.packages[pk]} index={pk + 1} cat={catalog} contact={catalog.contact} />;
    const p = catalog.products.find((x) => x.id === key);
    return <ProductCard ref={ref} product={p} index={indexOf(p)} total={included.length} contact={catalog.contact} markup={catalog.markup} vat={catalog.vat} pro={isPro(catalog, p.id)} treatments={catalog.treatments} />;
  };
  const fileName = (key) => {
    if (key === "cover") return "00_Coperta_Bond_Elisier.png";
    const pk = catalog.packages.findIndex((x) => x.id === key);
    if (pk >= 0) return `${String(included.length + pk + 1).padStart(2, "0")}_${slug(catalog.packages[pk].name)}.png`;
    const p = catalog.products.find((x) => x.id === key);
    return `${p.include ? String(indexOf(p)).padStart(2, "0") : "R2"}_${slug(`${p.line} ${p.name} ${p.size}`)}.png`;
  };

  const thumbs = [
    { key: "cover", label: "Copertă", missingPhoto: false },
    ...catalog.products.map((p) => ({ key: p.id, label: `${p.name.replace("Instant Bond Repair ", "")} ${p.size}`, missingPhoto: !p.image, excluded: !p.include })),
    ...catalog.packages.map((p) => ({ key: p.id, label: `${p.name} · -${p.discount}%`, missingPhoto: false })),
  ].map((t) => ({ ...t, render: (ref) => renderCard(t.key, ref), refCb: (n) => { nodes.current[t.key] = n; } }));

  const exportCurrent = async () => {
    toast.promise(downloadPng(nodes.current[selected], fileName(selected)).then(() => logEvent("png")), { loading: "Generez PNG 1080×1920…", success: "PNG descărcat", error: "Export eșuat" });
  };

  const exportPdf = () =>
    toast.promise(downloadPdf(), { loading: "Generez PDF…", success: "PDF descărcat", error: "Export eșuat" });

  const exportAll = async () => {
    const keys = ["cover", ...included.map((p) => p.id), ...catalog.packages.map((p) => p.id)];
    setZipProgress(0);
    try {
      await downloadZip(keys.map((k) => ({ node: nodes.current[k], filename: fileName(k) })), "Medavita_Bond_Elisier_WhatsApp.zip", (i) => setZipProgress(i));
      logEvent("zip");
      toast.success(`${keys.length} imagini exportate`);
    } catch {
      toast.error("Export eșuat");
    }
    setZipProgress(null);
  };

  const current = catalog.products.find((x) => x.id === selected);
  const currentPack = catalog.packages.find((x) => x.id === selected);
  const setPack = (pk) => update((c) => ({ packages: c.packages.map((x) => (x.id === pk.id ? pk : x)) }));
  const missing = included.filter((p) => !p.image).length;

  if (!isAdmin || asAgent) return (
    <div className="st-app st-agent-bf" data-testid="agent-bf-page">
      {asAgent && (
        <div className="st-as-agent" data-testid="as-agent-banner">
          Previzualizare: așa vede un agent contul lui. <a href="/" data-testid="as-agent-exit">Înapoi la admin</a>
        </div>
      )}
      <header className="st-top">
        <div className="st-brand">
          <span className="st-brand-mark">Black <em>Friday</em></span>
          <span className="st-brand-sub">Beauty Trends 2026 · contul tău de agent</span>
        </div>
        <div className="st-top-actions">
          <span className="st-status" data-testid="save-status">{saved ? <><Check size={14} /> Salvat</> : <><Loader2 size={14} className="animate-spin" /> Se salvează</>}</span>
          {!asAgent && <button type="button" className="st-btn st-btn--ghost" onClick={() => signOut({ redirectUrl: "/sign-in" })} data-testid="agent-signout"><LogOut size={16} /> Ieșire</button>}
        </div>
      </header>
      <main className="st-agent-main">
        <BFAgentBox slug={me.share_slug} phone={me.phone} name={me.name} profile={(
          <section className="st-agent-profile" data-testid="agent-panel">
            <h3>Datele tale de pe carduri</h3>
            <ProfileFields contact={profile} onContact={onContact} agent />
            <p className="st-hint">Numele și telefonul tău apar pe cardurile Black Friday și pe pagina pe care o deschid saloanele din linkul tău.</p>
          </section>
        )} />
      </main>
    </div>
  );

  return (
    <div className="st-app">
      <header className="st-top">
        <div className="st-brand">
          <span className="st-brand-mark">Instant <em>Bond Repair</em></span>
          <span className="st-brand-sub">Mini-catalog WhatsApp · 1080×1920</span>
        </div>
        <div className="st-top-actions">
          <span className="st-status" data-testid="save-status">
            {saved ? <><Check size={14} /> Salvat</> : <><Loader2 size={14} className="animate-spin" /> Se salvează</>}
          </span>
          {isAdmin && (
            <Link to="/utilizatori" className="st-btn st-btn--ghost" data-testid="users-link">
              <Users size={16} /> Agenți
            </Link>
          )}
          {isAdmin && (
            <a href="/?ca=agent" className="st-btn st-btn--ghost" data-testid="view-as-agent-link">
              <Eye size={16} /> Vezi ca agent
            </a>
          )}
          {isAdmin && (
            <button type="button" className="st-btn st-btn--ghost" data-testid="view-as-salon-link" onClick={() => {
              const w = window.open("", "_blank");
              fetchBFAdmin().then((c) => { w.location.href = `${bfPublicUrl()}?preview=${c.preview_token}&demo=1`; }).catch(() => w.close());
            }}>
              <Eye size={16} /> Vezi ca salon
            </button>
          )}
          {isAdmin && (
            <Link to="/black-friday-admin" className="st-btn st-btn--ghost bf-link" data-testid="bf-admin-link">
              <Tag size={16} /> Black Friday <UnassignedDot />
            </Link>
          )}
          <button type="button" className="st-btn st-btn--ghost" onClick={exportPdf} data-testid="export-pdf-btn">
            <FileText size={16} /> PDF prețuri
          </button>
          <button type="button" className="st-btn st-btn--ghost" onClick={exportCurrent} data-testid="export-current-btn">
            <Download size={16} /> PNG card
          </button>
          <button type="button" className="st-btn st-btn--silver" onClick={exportAll} disabled={zipProgress !== null} data-testid="export-all-btn">
            {zipProgress !== null ? <><Loader2 size={16} className="animate-spin" /> {zipProgress}/{included.length + 1 + catalog.packages.length}</> : <><Archive size={16} /> Export toate (ZIP)</>}
          </button>
          <span className="st-user" data-testid="user-menu"><UserButton /></span>
        </div>
      </header>

      <main className="st-main">
        {!isAdmin && <div className="st-bf-top"><BFAgentBox slug={me.share_slug} phone={me.phone} name={me.name} /></div>}
        <Thumbnails items={thumbs} selected={selected} onSelect={setSelected} />
        <section className="st-stage" data-testid="card-stage">
          <ScaledCard key={selected} width={stageWidth}>{renderCard(selected)}</ScaledCard>
          <p className="st-stage-note">
            {included.length} produse în catalog{missing > 0 && ` · ${missing} fără poză finală`}
          </p>
        </section>
        <aside className="st-panel">
          <div className="st-panel-head">
            <span className="st-eyebrow">{!isAdmin ? "Agent" : current ? "Card produs" : currentPack ? "Pachet" : "Copertă & contact"}</span>
            <h2 data-testid="panel-title">{!isAdmin ? "Datele tale" : current ? `${current.name} ${current.size}` : currentPack ? currentPack.name : "Instant Bond Repair"}</h2>
          </div>
          <div className="st-share-wrap">{!isAdmin && <div className="st-bf-side"><BFAgentBox slug={me.share_slug} phone={me.phone} name={me.name} /></div>}<ShareLink slug={me.share_slug} packages={catalog.packages} interested={!isAdmin} /></div>
          {!isAdmin ? (
            <div className="st-panel-body" data-testid="agent-panel">
              <ProfileFields contact={profile} onContact={onContact} />
              <p className="st-hint">Datele tale apar doar pe materialele descărcate de tine. Prețurile și pachetele sunt setate de administrator.</p>
            </div>
          ) : currentPack ? (
            <PackagePanel pack={currentPack} cat={catalog} onChange={setPack} />
          ) : current ? (
            <ProductPanel product={current} onChange={setProduct} onUpload={upload(current.id)} onRemove={() => setImage(current.id, null)} />
          ) : (
            <CoverPanel
              cover={catalog.cover}
              contact={catalog.contact}
              markup={catalog.markup}
              onMarkup={(markup) => update(() => ({ markup }))}
              vat={catalog.vat}
              onVat={(vat) => update(() => ({ vat }))}
              treatments={catalog.treatments}
              servicePrice={catalog.service_price}
              onTreat={(patch) => update(() => patch)}
              onCover={(cover) => update(() => ({ cover }))}
              onContact={onContact}
              onUpload={upload("cover")}
              onRemove={() => setImage("cover", null)}
            />
          )}
        </aside>
      </main>
    </div>
  );
}

